/**
 * 📋 WAITLIST REQUESTS PAGE - View and manage user waitlist submissions
 */

import { useState, useEffect } from 'react';
import apiClient from '@/services/apiClient';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Paper,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import {
  Search,
  Refresh,
  CheckCircle,
  Cancel as CancelIcon,
  Delete,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

interface WaitlistRequest {
  id: string;
  email?: string;
  phone?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt?: string;
  source?: string;
}

export default function WaitlistRequestsPage() {
  const [requests, setRequests] = useState<WaitlistRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const url = `/api/v1/admin/beta-invitations${params.toString() ? `?${params}` : ''}`;
      const response = await apiClient.get(url);
      const data = response.data;

      if (data.success) {
        const requestsList = data.data?.invitations || data.invitations || data.data || [];
        if (Array.isArray(requestsList)) {
          setRequests(requestsList);
        } else {
          setRequests([]);
        }
      } else {
        console.error('Failed to load waitlist requests:', data);
        setRequests([]);
        toast.error(data.error?.message || data.message || 'Failed to load waitlist requests');
      }
    } catch (error: any) {
      console.error('Error loading waitlist requests:', error);
      setRequests([]);
      toast.error(error.response?.data?.error?.message || 'Failed to load waitlist requests');
    } finally {
      setIsLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {

      const response = await apiClient.put(`/api/v1/admin/beta-invitations/${requestId}`, {
        status: newStatus
      });

      if (response.data.success) {
        toast.success(`Request ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
        loadRequests();
      } else {
        toast.error(response.data.error || 'Failed to update request status');
      }
    } catch (error: any) {
      console.error('Error updating request status:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update request status');
    }
  };

  const deleteRequest = async (requestId: string) => {
    if (!confirm('Are you sure you want to delete this waitlist request?')) {
      return;
    }

    try {

      const response = await apiClient.delete(`/api/v1/admin/beta-invitations/${requestId}`);

      if (response.data.success) {
        toast.success('Request deleted successfully');
        loadRequests();
      } else {
        toast.error(response.data.error || 'Failed to delete request');
      }
    } catch (error: any) {
      console.error('Error deleting request:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to delete request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  };

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.email?.toLowerCase().includes(query) ||
      req.phone?.toLowerCase().includes(query)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Waitlist Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage user submissions from the mobile app waitlist
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by email or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadRequests()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadRequests}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Waitlist Submissions ({filteredRequests.length})
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.map((req) => (
                  <TableRow key={req.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {req.email || req.phone}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {req.email ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Email fontSize="inherit" /> Email
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Phone fontSize="inherit" /> Phone
                              </Box>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        size="small"
                        color={getStatusColor(req.status) as any}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(req.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {req.source || 'Mobile App'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {req.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => updateRequestStatus(req.id, 'approved')}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => updateRequestStatus(req.id, 'rejected')}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteRequest(req.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {filteredRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Paper sx={{ p: 6, bgcolor: 'action.hover' }}>
                        <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No waitlist requests yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Users who submit the waitlist form on mobile will appear here
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
